import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Regulation } from "@/lib/types";
import { PageHeader, Table, StatusBadge } from "@/admin/ui";
import { LinkReviewQueue } from "@/admin/LinkReview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { title: "", summary: "", why_it_matters: "", countries: "", agencies: "", topics: "",
  status: "proposed", impact_level: "medium", effective_date: "", status_correction: false, status_note: "" };

export default function Regulations() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Regulation | null>(null);
  const [form, setForm] = useState<any>(EMPTY);

  const { data: regs } = useQuery({
    queryKey: ["admin-regulations"],
    queryFn: async () => (await api.get("/admin/regulations")).data,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-regulations"] });
    queryClient.invalidateQueries({ queryKey: ["regulations"] });
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r: Regulation) => {
    setEditing(r);
    setForm({ title: r.title, summary: r.summary, why_it_matters: r.why_it_matters || "",
      countries: r.countries.join(", "), agencies: r.agencies.join(", "), topics: r.topics.join(", "),
      status: r.status, impact_level: r.impact_level, effective_date: r.effective_date || "",
      status_correction: false, status_note: "" });
    setOpen(true);
  };

  const save = async () => {
    const csv = (s: string) => s.split(",").map((x: string) => x.trim()).filter(Boolean);
    const statusChanged = editing && form.status !== editing.status;
    const payload: any = { title: form.title, summary: form.summary, why_it_matters: form.why_it_matters || null,
      countries: csv(form.countries), agencies: csv(form.agencies), topics: csv(form.topics),
      status: form.status, impact_level: form.impact_level, effective_date: form.effective_date || null };
    if (statusChanged && form.status_correction) payload.status_correction = true;
    if (statusChanged && form.status_note) payload.status_note = form.status_note;
    try {
      if (editing) await api.patch(`/admin/regulations/${editing.id}`, payload);
      else await api.post("/admin/regulations", payload);
      toast.success(editing ? "Regulation updated" : "Regulation created");
      setOpen(false);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (r: Regulation) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await api.delete(`/admin/regulations/${r.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-regulations-page">
      <PageHeader title="Regulation Admin" sub="Maintain the structured policy intelligence database"
        actions={<Button onClick={openCreate} data-testid="add-regulation-button"
          className="bg-[#7C3AED] text-white font-bold hover:bg-[#6d2fd5]"><Plus className="h-4 w-4 mr-1" /> Add entry</Button>} />

      <LinkReviewQueue />

      <Table headers={["Title", "Jurisdiction", "Status", "Impact", "Actions"]}>
        {(regs || []).map((r: Regulation) => (
          <tr key={r.id} data-testid={`regulation-admin-row-${r.id}`}>
            <td className="px-4 py-2.5 text-white max-w-[380px] truncate">{r.title}</td>
            <td className="px-4 py-2.5 text-gray-400 text-xs">{r.countries.join(", ")}</td>
            <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
            <td className="px-4 py-2.5 font-mono text-xs text-gray-400 uppercase">{r.impact_level}</td>
            <td className="px-4 py-2.5">
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`regulation-edit-${r.id}`}
                  className="text-gray-400 hover:text-white h-8 w-8"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(r)} data-testid={`regulation-delete-${r.id}`}
                  className="text-gray-500 hover:text-red-400 h-8 w-8"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </td>
          </tr>
        ))}
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit regulation" : "New regulation entry"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="regulation-form-title" className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <div><Label>Summary</Label>
              <Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
                data-testid="regulation-form-summary" className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <div><Label>Why it matters</Label>
              <Textarea value={form.why_it_matters} onChange={(e) => setForm({ ...form, why_it_matters: e.target.value })}
                className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Countries (csv)</Label>
                <Input value={form.countries} onChange={(e) => setForm({ ...form, countries: e.target.value })}
                  data-testid="regulation-form-countries" className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
              <div><Label>Agencies (csv)</Label>
                <Input value={form.agencies} onChange={(e) => setForm({ ...form, agencies: e.target.value })}
                  className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            </div>
            <div><Label>Topics (csv)</Label>
              <Input value={form.topics} onChange={(e) => setForm({ ...form, topics: e.target.value })}
                className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {["proposed", "in_progress", "enacted", "enforced", "rejected"].map((s) =>
                      <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>Impact</Label>
                <Select value={form.impact_level} onValueChange={(v) => setForm({ ...form, impact_level: v })}>
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {["low", "medium", "high", "critical"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>Effective date</Label>
                <Input type="date" value={form.effective_date}
                  onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                  className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            </div>
            {editing && form.status !== editing.status && (
              <div className="rounded-lg border border-[#A78BFA]/30 bg-[#A78BFA]/[0.06] p-3 space-y-2"
                data-testid="status-change-panel">
                <p className="font-mono text-[10px] font-bold uppercase tracking-wide text-[#A78BFA]">
                  Status change: {editing.status} → {form.status} · recorded in status history
                </p>
                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={form.status_correction}
                    onChange={(e) => setForm({ ...form, status_correction: e.target.checked })}
                    data-testid="status-correction-checkbox" className="accent-[#A78BFA]" />
                  Editorial correction — fixing wrongly stored data, NOT a real-world status change
                </label>
                <Input value={form.status_note} placeholder="Optional note / reason"
                  onChange={(e) => setForm({ ...form, status_note: e.target.value })}
                  data-testid="status-note-input" className="bg-[#1A1A1A] border-white/10 text-xs" />
              </div>
            )}
            <Button onClick={save} data-testid="regulation-form-save"
              className="w-full bg-[#7C3AED] text-white font-bold hover:bg-[#6d2fd5]">
              {editing ? "Save changes" : "Create entry"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
